import React, { useState, useEffect } from 'react';
import { 
  Bell, ChevronLeft, Plus, Calendar, Droplet, Heart, Moon, Sun, 
  Pill, Clock, Trash2, ToggleLeft, ToggleRight, AlertCircle, 
  ChevronDown, ChevronUp, Settings
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import BottomNav from '../components/BottomNav';
import api from '../services/api';
import { toast } from '../hooks/use-toast';
import { useNavigate } from 'react-router-dom';

const CustomAlerts = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [customAlerts, setCustomAlerts] = useState([]);
  const [upcomingAlerts, setUpcomingAlerts] = useState([]);
  const [alertLogs, setAlertLogs] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [expandedSection, setExpandedSection] = useState('cycle');
  
  const [newAlert, setNewAlert] = useState({
    type: 'custom',
    title: '',
    message: '',
    triggerRule: 'days_before_period',
    daysValue: 3,
    time: '09:00',
    recurring: true
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [settingsRes, alertsRes, previewRes, logsRes] = await Promise.all([
        api.get('/alerts/settings'),
        api.get('/alerts/custom'),
        api.get('/alerts/preview'),
        api.get('/alerts/logs?limit=10')
      ]);
      setSettings(settingsRes.data.settings);
      setCustomAlerts(alertsRes.data.alerts || []);
      setUpcomingAlerts(previewRes.data.alerts || []);
      setAlertLogs(logsRes.data.logs || []);
    } catch (error) {
      console.error('Error fetching alerts:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les paramètres',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const updateSettings = async (newSettings) => {
    try {
      await api.put('/alerts/settings', newSettings);
      setSettings(newSettings);
      toast({ title: 'Paramètres mis à jour' });
      // Refresh preview
      const previewRes = await api.get('/alerts/preview');
      setUpcomingAlerts(previewRes.data.alerts || []);
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de sauvegarder',
        variant: 'destructive'
      });
    }
  };

  const handleCycleAlertToggle = (alertType, field, value) => {
    const newSettings = {
      ...settings,
      cycleAlerts: {
        ...settings.cycleAlerts,
        [alertType]: {
          ...settings.cycleAlerts[alertType],
          [field]: value
        }
      }
    };
    updateSettings(newSettings);
  };

  const handleContraceptionToggle = (field, value) => {
    const newSettings = {
      ...settings,
      contraceptionAlerts: {
        ...settings.contraceptionAlerts,
        [field]: value
      }
    };
    updateSettings(newSettings);
  };

  const handleAddCustomAlert = async () => {
    if (!newAlert.title) {
      toast({ title: 'Erreur', description: 'Le titre est requis', variant: 'destructive' });
      return;
    }
    
    try {
      await api.post('/alerts/create-custom', newAlert);
      toast({ title: 'Alerte créée' });
      setShowAddDialog(false);
      setNewAlert({
        type: 'custom',
        title: '',
        message: '',
        triggerRule: 'days_before_period',
        daysValue: 3,
        time: '09:00',
        recurring: true
      });
      fetchData();
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Erreur lors de la création',
        variant: 'destructive'
      });
    }
  };

  const handleDeleteAlert = async (alertId) => {
    if (!window.confirm('Supprimer cette alerte ?')) return;
    
    try {
      await api.delete(`/alerts/custom/${alertId}`);
      toast({ title: 'Alerte supprimée' });
      fetchData();
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const handleToggleAlert = async (alertId) => {
    try {
      await api.put(`/alerts/custom/${alertId}/toggle`);
      fetchData();
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case 'menstruation': return <Droplet className="w-5 h-5 text-red-400" />;
      case 'ovulation': return <Sun className="w-5 h-5 text-yellow-400" />;
      case 'fertile_window': return <Heart className="w-5 h-5 text-green-400" />;
      case 'contraception': return <Pill className="w-5 h-5 text-blue-400" />;
      default: return <Bell className="w-5 h-5 text-pink-400" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 flex items-center justify-center">
        <div className="text-white text-xl">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-purple-900 pb-24">
      {/* Header */}
      <div className="sticky top-0 bg-purple-900/80 backdrop-blur-md border-b border-white/10 px-4 py-4 z-40">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/10 rounded-lg">
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <div className="flex items-center gap-2">
              <Bell className="w-6 h-6 text-pink-300" />
              <h1 className="text-xl font-bold text-white">Alertes personnalisées</h1>
            </div>
          </div>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-pink-500 hover:bg-pink-600">
                <Plus className="w-4 h-4 mr-1" />
                Créer
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-purple-900 border-purple-700 max-w-md max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-white text-xl flex items-center gap-2">
                  <Bell className="w-6 h-6 text-pink-400" />
                  Nouvelle alerte
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-pink-200">Titre *</Label>
                  <Input
                    placeholder="Ex: Rappel pilule"
                    value={newAlert.title}
                    onChange={(e) => setNewAlert({...newAlert, title: e.target.value})}
                    className="bg-white/10 border-white/20 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-pink-200">Message (optionnel)</Label>
                  <Input
                    placeholder="Ex: N'oublie pas ta pilule!"
                    value={newAlert.message}
                    onChange={(e) => setNewAlert({...newAlert, message: e.target.value})}
                    className="bg-white/10 border-white/20 text-white mt-1"
                  />
                </div>
                <div>
                  <Label className="text-pink-200">Déclencheur</Label>
                  <Select 
                    value={newAlert.triggerRule} 
                    onValueChange={(val) => setNewAlert({...newAlert, triggerRule: val})}
                  >
                    <SelectTrigger className="bg-white/10 border-white/20 text-white mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="days_before_period">Jours avant les règles</SelectItem>
                      <SelectItem value="days_before_ovulation">Jours avant l'ovulation</SelectItem>
                      <SelectItem value="days_before_fertile">Jours avant fenêtre fertile</SelectItem>
                      <SelectItem value="days_before_renewal">Jours avant renouvellement contraception</SelectItem>
                      <SelectItem value="daily">Quotidien</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {newAlert.triggerRule !== 'daily' && (
                  <div>
                    <Label className="text-pink-200">Nombre de jours avant</Label>
                    <Input
                      type="number"
                      min="1"
                      max="14"
                      value={newAlert.daysValue}
                      onChange={(e) => setNewAlert({...newAlert, daysValue: parseInt(e.target.value)})}
                      className="bg-white/10 border-white/20 text-white mt-1 w-24"
                    />
                  </div>
                )}
                <div>
                  <Label className="text-pink-200">Heure</Label>
                  <Input
                    type="time"
                    value={newAlert.time}
                    onChange={(e) => setNewAlert({...newAlert, time: e.target.value})}
                    className="bg-white/10 border-white/20 text-white mt-1 w-32"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <Label className="text-pink-200">Récurrent (chaque cycle)</Label>
                  <Switch
                    checked={newAlert.recurring}
                    onCheckedChange={(val) => setNewAlert({...newAlert, recurring: val})}
                  />
                </div>
                <Button onClick={handleAddCustomAlert} className="w-full bg-gradient-to-r from-pink-500 to-purple-500">
                  Créer l'alerte
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6 space-y-4">
        {/* Upcoming Alerts Preview */}
        {upcomingAlerts.length > 0 && (
          <Card className="p-4 bg-gradient-to-r from-pink-500/20 to-purple-500/20 border-pink-400/30">
            <h3 className="text-white font-bold mb-3 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-pink-400" />
              Prochaines alertes
            </h3>
            <div className="space-y-2">
              {upcomingAlerts.slice(0, 5).map((alert, index) => (
                <div key={index} className="flex items-center gap-3 p-2 bg-white/5 rounded-lg">
                  {getAlertIcon(alert.type)}
                  <div className="flex-1">
                    <p className="text-white text-sm font-medium">{alert.title}</p>
                    <p className="text-pink-200 text-xs">{formatDate(alert.date)}</p>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Cycle Alerts Section */}
        <Card className="bg-white/10 backdrop-blur-md border-white/20 overflow-hidden">
          <button 
            onClick={() => setExpandedSection(expandedSection === 'cycle' ? '' : 'cycle')}
            className="w-full p-4 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <Droplet className="w-5 h-5 text-red-400" />
              </div>
              <div className="text-left">
                <h3 className="text-white font-bold">Alertes Cycle</h3>
                <p className="text-pink-200 text-xs">Règles, ovulation, fertilité</p>
              </div>
            </div>
            {expandedSection === 'cycle' ? 
              <ChevronUp className="w-5 h-5 text-white" /> : 
              <ChevronDown className="w-5 h-5 text-white" />
            }
          </button>
          
          {expandedSection === 'cycle' && settings && (
            <div className="px-4 pb-4 space-y-3">
              {/* Menstruation Alert */}
              <div className="p-3 bg-white/5 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Droplet className="w-4 h-4 text-red-400" />
                    <span className="text-white text-sm">Début des règles</span>
                  </div>
                  <Switch
                    checked={settings.cycleAlerts?.menstruationStart?.enabled}
                    onCheckedChange={(val) => handleCycleAlertToggle('menstruationStart', 'enabled', val)}
                  />
                </div>
                {settings.cycleAlerts?.menstruationStart?.enabled && (
                  <div className="flex items-center gap-2 ml-6">
                    <span className="text-pink-200 text-xs">Prévenir</span>
                    <Input
                      type="number"
                      min="1"
                      max="7"
                      value={settings.cycleAlerts?.menstruationStart?.daysBefore || 3}
                      onChange={(e) => handleCycleAlertToggle('menstruationStart', 'daysBefore', parseInt(e.target.value))}
                      className="bg-white/10 border-white/20 text-white w-16 h-7 text-sm"
                    />
                    <span className="text-pink-200 text-xs">jours avant</span>
                  </div>
                )}
              </div>

              {/* Ovulation Alert */}
              <div className="p-3 bg-white/5 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Sun className="w-4 h-4 text-yellow-400" />
                    <span className="text-white text-sm">Ovulation</span>
                  </div>
                  <Switch
                    checked={settings.cycleAlerts?.ovulation?.enabled}
                    onCheckedChange={(val) => handleCycleAlertToggle('ovulation', 'enabled', val)}
                  />
                </div>
                {settings.cycleAlerts?.ovulation?.enabled && (
                  <div className="flex items-center gap-2 ml-6">
                    <span className="text-pink-200 text-xs">Prévenir</span>
                    <Input
                      type="number"
                      min="0"
                      max="3"
                      value={settings.cycleAlerts?.ovulation?.daysBefore || 1}
                      onChange={(e) => handleCycleAlertToggle('ovulation', 'daysBefore', parseInt(e.target.value))}
                      className="bg-white/10 border-white/20 text-white w-16 h-7 text-sm"
                    />
                    <span className="text-pink-200 text-xs">jour(s) avant</span>
                  </div>
                )}
              </div>

              {/* Fertile Window Alert */}
              <div className="p-3 bg-white/5 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Heart className="w-4 h-4 text-green-400" />
                    <span className="text-white text-sm">Fenêtre fertile</span>
                  </div>
                  <Switch
                    checked={settings.cycleAlerts?.fertileWindow?.enabled}
                    onCheckedChange={(val) => handleCycleAlertToggle('fertileWindow', 'enabled', val)}
                  />
                </div>
                {settings.cycleAlerts?.fertileWindow?.enabled && (
                  <div className="flex items-center gap-2 ml-6">
                    <span className="text-pink-200 text-xs">Prévenir</span>
                    <Input
                      type="number"
                      min="0"
                      max="3"
                      value={settings.cycleAlerts?.fertileWindow?.daysBefore || 1}
                      onChange={(e) => handleCycleAlertToggle('fertileWindow', 'daysBefore', parseInt(e.target.value))}
                      className="bg-white/10 border-white/20 text-white w-16 h-7 text-sm"
                    />
                    <span className="text-pink-200 text-xs">jour(s) avant</span>
                  </div>
                )}
              </div>

              {/* Luteal Phase Alert */}
              <div className="p-3 bg-white/5 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Moon className="w-4 h-4 text-purple-400" />
                    <span className="text-white text-sm">Phase lutéale</span>
                  </div>
                  <Switch
                    checked={settings.cycleAlerts?.lutealPhase?.enabled}
                    onCheckedChange={(val) => handleCycleAlertToggle('lutealPhase', 'enabled', val)}
                  />
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Contraception Alerts Section */}
        <Card className="bg-white/10 backdrop-blur-md border-white/20 overflow-hidden">
          <button 
            onClick={() => setExpandedSection(expandedSection === 'contraception' ? '' : 'contraception')}
            className="w-full p-4 flex items-center justify-between"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                <Pill className="w-5 h-5 text-blue-400" />
              </div>
              <div className="text-left">
                <h3 className="text-white font-bold">Alertes Contraception</h3>
                <p className="text-pink-200 text-xs">Renouvellement, rappels</p>
              </div>
            </div>
            {expandedSection === 'contraception' ? 
              <ChevronUp className="w-5 h-5 text-white" /> : 
              <ChevronDown className="w-5 h-5 text-white" />
            }
          </button>
          
          {expandedSection === 'contraception' && settings && (
            <div className="px-4 pb-4 space-y-3">
              <div className="p-3 bg-white/5 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-white text-sm">Activer les rappels</span>
                  <Switch
                    checked={settings.contraceptionAlerts?.enabled}
                    onCheckedChange={(val) => handleContraceptionToggle('enabled', val)}
                  />
                </div>
                
                {settings.contraceptionAlerts?.enabled && (
                  <>
                    <div className="space-y-2 mb-3">
                      <p className="text-pink-200 text-xs">Rappeler avant renouvellement:</p>
                      <div className="flex flex-wrap gap-2">
                        {[2, 3, 5, 7].map((days) => (
                          <button
                            key={days}
                            onClick={() => {
                              const current = settings.contraceptionAlerts?.reminderDays || [];
                              const newDays = current.includes(days) 
                                ? current.filter(d => d !== days)
                                : [...current, days];
                              handleContraceptionToggle('reminderDays', newDays);
                            }}
                            className={`px-3 py-1 rounded-full text-xs ${
                              settings.contraceptionAlerts?.reminderDays?.includes(days)
                                ? 'bg-blue-500 text-white'
                                : 'bg-white/10 text-pink-200'
                            }`}
                          >
                            {days} jours
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-pink-200 text-xs">Rappel quotidien (pilule)</span>
                      <Switch
                        checked={settings.contraceptionAlerts?.dailyReminder}
                        onCheckedChange={(val) => handleContraceptionToggle('dailyReminder', val)}
                      />
                    </div>
                    
                    {settings.contraceptionAlerts?.dailyReminder && (
                      <div className="flex items-center gap-2 mt-2">
                        <Clock className="w-4 h-4 text-pink-300" />
                        <span className="text-pink-200 text-xs">Heure:</span>
                        <Input
                          type="time"
                          value={settings.contraceptionAlerts?.reminderTime || '09:00'}
                          onChange={(e) => handleContraceptionToggle('reminderTime', e.target.value)}
                          className="bg-white/10 border-white/20 text-white w-28 h-7 text-sm"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </Card>

        {/* Custom Alerts */}
        <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20">
          <h3 className="text-white font-bold mb-3 flex items-center gap-2">
            <Settings className="w-5 h-5 text-pink-400" />
            Mes alertes personnalisées
          </h3>
          
          {customAlerts.length === 0 ? (
            <div className="text-center py-6">
              <Bell className="w-12 h-12 text-pink-300/50 mx-auto mb-3" />
              <p className="text-pink-200 text-sm">Aucune alerte personnalisée</p>
              <p className="text-pink-200/60 text-xs mt-1">
                Créez des alertes adaptées à vos besoins
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {customAlerts.map((alert) => (
                <div key={alert.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Bell className="w-4 h-4 text-pink-400" />
                    <div>
                      <p className="text-white text-sm font-medium">{alert.title}</p>
                      <p className="text-pink-200 text-xs">
                        {alert.recurring ? 'Récurrent' : 'Une fois'} • {alert.time}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleAlert(alert.id)}
                      className="p-1 hover:bg-white/10 rounded"
                    >
                      {alert.enabled ? 
                        <ToggleRight className="w-5 h-5 text-green-400" /> : 
                        <ToggleLeft className="w-5 h-5 text-gray-400" />
                      }
                    </button>
                    <button
                      onClick={() => handleDeleteAlert(alert.id)}
                      className="p-1 hover:bg-red-500/20 rounded text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Alert History */}
        {alertLogs.length > 0 && (
          <Card className="p-4 bg-white/10 backdrop-blur-md border-white/20">
            <h3 className="text-white font-bold mb-3 flex items-center gap-2">
              <Clock className="w-5 h-5 text-green-400" />
              Notifications envoyées récemment
            </h3>
            <div className="space-y-2">
              {alertLogs.slice(0, 5).map((log) => (
                <div key={log.id} className="flex items-center justify-between p-2 bg-white/5 rounded-lg">
                  <div className="flex items-center gap-2">
                    <div className={`w-2 h-2 rounded-full ${log.pushResult?.success ? 'bg-green-400' : 'bg-yellow-400'}`} />
                    <span className="text-pink-200 text-sm">{log.alertType}</span>
                  </div>
                  <span className="text-pink-300 text-xs">
                    {new Date(log.sentAt).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Info */}
        <Card className="p-4 bg-blue-500/20 border-blue-400/30">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-300 shrink-0 mt-0.5" />
            <div>
              <h4 className="text-white font-medium text-sm">Notifications Push Automatiques</h4>
              <p className="text-blue-200 text-xs mt-1">
                Les alertes sont envoyées automatiquement chaque jour selon vos paramètres. 
                Les rappels de cycle sont envoyés à 8h, les rappels de contraception à 9h.
                Assurez-vous d'avoir autorisé les notifications dans l'application mobile.
              </p>
            </div>
          </div>
        </Card>
      </div>

      <BottomNav active="calendar" />
    </div>
  );
};

export default CustomAlerts;
